import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  faCheck,
  faExclamation,
  faInfoCircle,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { CheckCircle } from "react-bootstrap-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BloodMarkerCard } from "./BloodMarkerCard";
import "../../../assets/scss/_health-area-section.scss";
import { iconHelper } from "../../../utils/utils";
import { Collapse } from "react-bootstrap";
import { Tooltip } from "../../../stories/ToolTips/ToolTip";
import { Alert } from "../../../stories/Alerts/Alert";
import { formatReportedDateTime } from "../../../utils";
import { useMediaQuery } from "react-responsive";

/**
 * Get the status label based on the health area status.
 *
 * @param {string} status - The status of the health area.
 * @returns {string} The status label.
 */
const getStatusLabel = (status) => {
  if (status === "good") return "Everything looks good";
  if (status === "require-attention") return "Some results require attention";
  return "Some borderline results";
};

/**
 * Determine the sexual health status based on markers.
 *
 * @param {Array} markers - The array of markers to evaluate.
 * @returns {string} The status of sexual health.
 */

export const getStatusSexualHealth = (markers) => {
  let badCount = 0;
  let existUnconfirmedReactive = false;

  markers.forEach((marker) => {
    // Skip partial/untestable markers - they shouldn't count as "bad"
    if (marker.partial === "true") {
      return; // Skip this marker
    }

    // Only count markers with actual results that are not normal/green
    if (marker.result && ["normal", "green"].indexOf(marker.result) === -1) {
      badCount++;
    }

    if (String(marker.value).toLowerCase() === "unconfirmed reactive") {
      existUnconfirmedReactive = true;
    }
  });

  if (existUnconfirmedReactive) {
    return "retest";
  }

  return badCount > 0 ? "attention" : "normal";
};

/**
 * HealthAreaSection component for displaying health area information.
 * @returns {JSX.Element} The rendered health area section.
 */


const HealthAreaSection = ({
  area,
  area_status,
  doctors_notes,
  markers = [],
  hideHeader,
  hideTitle,
  hideIcon,
  collapseItems,
  type,
  isPartialResult,
  retestKit,
  folate_flag,
  original_kit_review_ts,
  retest_review_ts,
  area_partial_markers_only,
  isPartialArea,
  report_type,
  isRejected,
  partialResult,
  onSeeDetails,
}) => {
  const [allMarkersVisible, setAllMarkersVisible] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 700 });
  // Calculate partial markers and isPartial state using useMemo
  const { isPartial, partialMarkerNames } = useMemo(() => {
    const partialNames = markers
      .filter((marker) => marker && marker.partial === "true")
      .map((marker) => marker.name);

    return {
      isPartial: partialNames.length > 0,
      partialMarkerNames: partialNames,
    };
  }, [markers]);

  // Decide how to handle markers based on retestKit and area
  const sortedMarkers = useMemo(() => {
    if (
      retestKit &&
      folate_flag &&
      (area === "Vitamins & Minerals" || area === "Sexual Health")
    ) {
      return markers; // keep as is
    }

    // return a NEW sorted array to avoid mutating props
    return [...markers].sort((a, b) => a?.name?.localeCompare(b.name));
  }, [markers, retestKit, area]);

  const renderHeader = () => {
    const isSexualHealth = type?.includes("sex-health");
    const icon = iconHelper(`Biomarker ${area}`);
    let statusIcon = area_status === "good" ? faCheck : faExclamation;
    let statusLabel = getStatusLabel(area_status);

    if (isSexualHealth) {
      const testStatus = getStatusSexualHealth(markers);

      const groupStatusComment = {
        normal:
          "This test has not detected any presence of infection. Please continue to practice safe sex.",
        attention:
          "This test has detected the presence of an infection in your urine sample. This is a very treatable infection. You will require a course of medication from your GP. Early treatment is beneficial. Please do not delay.",
        retest:
          "This test has been unable to confirm the presence of an infection in your sample. You must attend a sexual health clinic or speak to your GP to arrange a confirmatory test. It is very easy to pass STIs to sexual partners and you must abstain from sexual contact until further investigations are complete.",
      }[testStatus];

      return (
        <header
          className="healthArea__header"
          data-testid="health-area-section"
        >
          <div className="healthArea__header-content">
            {/* <h2 className="healthArea__header-title">
              {area} &nbsp;
              {isPartial ? (
                <Tooltip
                  text={"Some biomarker (s) couldn't be analysed"}
                  placement="right"
                  tooltipClass="reports"
                >
                  <FontAwesomeIcon icon={faInfoCircle} color="#ad3b73" />
                </Tooltip>
              ) : (
                <span
                  className={`healthArea__header-status healthArea__header-status--${testStatus} `}
                >
                  <FontAwesomeIcon icon={statusIcon} />
                </span>
              )}
            </h2> */}
            {/* <p className="healthArea__header-subtitle">
              <span
                className={`healthArea__header-subtitle-label-sexual-health`}
              >
                <FontAwesomeIcon
                  icon={statusIcon}
                  className={`healthArea__header-status healthArea__header-status--${area_status}`}
                />
                {statusLabel} with your {area}
              </span>
            </p>
            <p>{groupStatusComment}</p> */}
          </div>
        </header>
      );
    }
   
    // if (report_type === "boots-blood-vitamin-b12" && isMobile) {
    //   return (
    //     <>
    //       <header className="healthArea__header healthArea__header--b12">
    //         <div className="healthArea__header-content--b12">
    //           {!isPartial && (
    //             <p
    //               className={`healthArea__header-subtitle healthArea__header-subtitle--${area_status}`}
    //             >
    //               <span
    //                 className={`healthArea__header-status--b12 healthArea__header-status--b12--${area_status} position-relative custom-top`}
    //               >
    //                 <FontAwesomeIcon
    //                   icon={statusIcon}
    //                 />
    //               </span>
    //               <span className="healthArea__header-subtitle-new">
    //                 {getStatusLabel(area_status)}
    //               </span>
    //             </p>
    //           )}
    //           {doctors_notes && <p>{doctors_notes}</p>}
    //         </div>
    //       </header>
    //       <hr className="markerCard__devider w-100"></hr>
    //     </>
    //   );
    // }

    if (type?.includes("sex-health")) {
      const testStatus = getStatusSexualHealth(markers);
      statusIcon = testStatus === "good" ? faCheck : faExclamation;
    }

    if (hideHeader) return null;
    if (isMobile) {
      return (
        // <header className="healthArea__header">
        //   {isMobile && (
        //     <div className="d-flex">
        //       {" "}
        //       {icon && !hideIcon && (
        //         <img
        //           src={icon}
        //           alt={area}
        //           className="healthArea__header-icon"
        //         />
        //       )}
        //       {!hideTitle && (
        //         <h2 className="healthArea__header-title">
        //           {area}{" "}
        //           {isPartial && (
        //             <Tooltip
        //               text={"Some biomarker (s) couldn't be analysed"}
        //               placement="right"
        //               tooltipClass="reports"
        //             >
        //               <FontAwesomeIcon icon={faInfoCircle} color="#ad3b73" />
        //             </Tooltip>
        //           )}
        //         </h2>
        //       )}
        //     </div>
        //   )}
        //   <div className="healthArea__header-content">
        //     {!isPartial && (
        //       <p
        //         className={`healthArea__header-subtitle healthArea__header-subtitle--${area_status}`}
        //       >
        //         {" "}
        //         <span
        //           className={`healthArea__header-status healthArea__header-status--${area_status} bg-transparent`}
        //         >
        //           <FontAwesomeIcon
        //             icon={statusIcon}
        //             className={`healthArea__header-status healthArea__header-status--${area_status} position-relative custom-top`}
        //           />
        //         </span>
        //         <span>{getStatusLabel(area_status)}</span>
        //       </p>
        //     )}
        //     {doctors_notes && <p>{doctors_notes}</p>}
        //   </div>
        // </header>
        <></>
      );
    }
  };

  const boldPartials = (() => {
    const bolded = partialMarkerNames.map((name) => `**${name}**`);
    if (bolded.length === 0) return "";
    if (bolded.length === 1) return bolded[0];
    if (bolded.length === 2) return `${bolded[0]} & ${bolded[1]}`;
    return `${bolded.slice(0, -1).join(", ")} **&** ${
      bolded[bolded.length - 1]
    }`;
  })();
  let firstMarkers =
    collapseItems && sortedMarkers?.length
      ? sortedMarkers.slice(0, 3)
      : sortedMarkers;

  if (!firstMarkers?.length) firstMarkers = [];
  const otherMarkers = sortedMarkers?.length ? sortedMarkers.slice(3) : [];

  return (
    <section className="healthArea__container">
      {renderHeader()}

      {/* Render list of markers */}
      <div className={`healthArea__list`}>
        {retestKit ? (
          <>
            {/* Folate first */}
            {firstMarkers.find(
              (m) =>
                (m.name === "HIV" || m.name === "Syphilis") &&
                (!m.partial || m.partial !== "true")
            ) && (
              <p className={"text-success"}>
                {" "}
                <CheckCircle /> <strong>Reported on: </strong>
                {formatReportedDateTime(retest_review_ts)}
              </p>
            )}

            {/* Folate, HIV, Syphilis first */}
              {["Folate", "HIV", "Syphilis"].map((markerName) => {
                const marker = firstMarkers.find((m) => m.name === markerName);
                if (!marker) return null;
                return (
                  folate_flag !== false && (
                    <div key={marker.code}>
                      {((marker.name === "Folate" &&
                        area === "Vitamins & Minerals Profile Test") ||
                        area === "Vitamins & Minerals") && (
                        <p className="text-success">
                          <CheckCircle />{" "} <strong>Reported on: </strong>
                          {formatReportedDateTime(retest_review_ts)}
                        </p>
                      )}
                      <BloodMarkerCard
                        marker={marker}
                        original_kit_review_ts={original_kit_review_ts}
                        retest_review_ts={retest_review_ts}
                        report_type={report_type}
                        onSeeDetails={onSeeDetails}
                      />
                    </div>
                  )
                );
              })}
            {/* Rest of the markers excluding Folate */}
            {firstMarkers.filter(
              (m) =>
                m.name !== "Folate" && m.name !== "HIV" && m.name !== "Syphilis"
            ).length > 0 &&
              retest_review_ts &&
              (area_partial_markers_only.some(
                (a) => a.area === area && area !== "Vitamins & Minerals"
              ) ? (
                <p className={"text-success"}>
                  <CheckCircle /> <strong>Reported on: </strong>
                  {formatReportedDateTime(retest_review_ts)}
                </p>
              ) : (
                <p className="text-danger">
                  <FontAwesomeIcon icon={faClockRotateLeft} />{" "}
                  <strong>Reported on: </strong>
                  {formatReportedDateTime(original_kit_review_ts)}
                </p>
              ))}

            {firstMarkers
              .filter((m) => (folate_flag ? m.name !== "Folate" :true))
              .map((marker) =>
                marker?.name === "HIV" ||
                marker?.name === "Syphilis" ||
                ((area === "Full blood count" || area === "Glocose") &&
                  isPartialArea === true) ? null : (
                  <BloodMarkerCard
                    key={marker.code}
                    marker={marker}
                    original_kit_review_ts={original_kit_review_ts}
                    retest_review_ts={retest_review_ts}
                    report_type={report_type}
                    onSeeDetails={onSeeDetails}
                  />
                )
              )}
          </>
        ) : (
             (area === "Full blood count" || area === "Glocose") &&
            (isPartialArea === true || partialResult === true) && isRejected === true
              ? null
              : firstMarkers.map((marker) => (
                  <BloodMarkerCard key={marker.code} marker={marker} report_type={report_type} onSeeDetails={onSeeDetails}/>
                ))
        )}
        {(area === "Full blood count" || area === "Glocose") &&
        isPartialArea === true ? null : (
          <Collapse in={Boolean(allMarkersVisible)}>
            <div>
              {Array.isArray(otherMarkers) &&
                otherMarkers.map((marker) =>
                  marker ? (
                    <BloodMarkerCard key={marker.code} marker={marker} report_type={report_type} onSeeDetails={onSeeDetails}/>
                  ) : null
                )}
            </div>
          </Collapse>
        )}

        {collapseItems &&
          otherMarkers.length > 0 &&
          ((area === "Full blood count" || area === "Glocose") &&
          isPartialArea === true ? null : (
            <button
              className="healthArea__button"
              onClick={() => setAllMarkersVisible(!allMarkersVisible)}
            >
              {allMarkersVisible
                ? "Show less"
                : `Show all (${otherMarkers.length})`}
            </button>
          ))}
        {/* {} */}
        {retestKit &&
        isPartial &&
        (boldPartials.includes("Folate") ||
          boldPartials.includes("HIV") ||
          boldPartials.includes("Syphilis")) ? (
          <p className={"text-success mt-2 mb-0"}>
            <CheckCircle /> <strong>Reported on: </strong>
            {formatReportedDateTime(retest_review_ts)}
          </p>
        ) : null}
        {isPartial && area !== "Full blood count" && (
          <Alert
            className="partial-alert"
            fontSize={boldPartials.split(",").length < 2 ? "19px" : "30px"}
            variant="light"
            text={`${boldPartials} - could not be reported.`}
          />
        )}
      </div>
    </section>
  );
};

HealthAreaSection.propTypes = {
  area: PropTypes.string.isRequired,
  area_status: PropTypes.string.isRequired,
  doctors_notes: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      result: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  hideHeader: PropTypes.bool,
  hideTitle: PropTypes.bool,
  hideIcon: PropTypes.bool,
  collapseItems: PropTypes.bool,
  type: PropTypes.string,
  isPartialResult: PropTypes.bool,
  retestKit: PropTypes.bool,
  folate_flag: PropTypes.bool,
  original_kit_review_ts: PropTypes.string,
  retest_review_ts: PropTypes.string,
  area_partial_markers_only: PropTypes.array,
  isPartialArea: PropTypes.bool,
  report_type: PropTypes.string,
  isRejected: PropTypes.bool,
  partialResult: PropTypes.bool,
  onSeeDetails: PropTypes.func,
};

export default HealthAreaSection;
